import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { inventoryAPI, settingsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";

export default function SpecSheet() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  useEffect(() => {
    inventoryAPI.get(id).then(r => { setItem(r.data); setLoading(false); }).catch(() => setLoading(false));
    settingsAPI.getBranding().then(r => {
      if (r.data?.primary_logo) setLogoUrl(resolveImageUrl(r.data.primary_logo));
    }).catch(() => {});
  }, [id]);

  if (loading) return <div style={{ padding: 48, textAlign: 'center', fontFamily: "'Open Sans', sans-serif", fontSize: 18 }}>Loading spec sheet...</div>;
  if (!item) return <div style={{ padding: 48, textAlign: 'center', fontFamily: "'Open Sans', sans-serif", fontSize: 18 }}>Unit not found</div>;

  const images = item.images || [];
  const price = item.price ? `$${item.price.toLocaleString()}` : '';
  const title = item.title || `${item.year} ${item.make} ${item.model}`;

  const truckSpecs = [
    ['Stock #', item.stock_number], ['Odometer', item.mileage_km ? `${item.mileage_km.toLocaleString()} KM` : null],
    ['Engine', item.engine], ['Horsepower', item.horsepower], ['Transmission', item.transmission],
    ['Axles', item.axles], ['Suspension', item.suspension], ['Rear Ratio', item.rear_ratio],
    ['Wheelbase', item.wheelbase], ['GVWR', item.gvwr], ['Body Type', item.body_type],
    ['Condition', item.condition], ['Location', item.location],
  ];

  const trailerSpecs = [
    ['Stock #', item.stock_number], ['Year', item.year], ['Make', item.make], ['Model', item.model],
    ['Condition', item.condition], ['Body Type', item.body_type], ['Length', item.trailer_length],
    ['Axles', item.axle_count], ['Suspension', item.suspension], ['Roof', item.roof_type],
    ['Doors', item.door_type], ['Reefer', item.reefer_details], ['Location', item.location],
  ];

  const specs = (item.type === 'trailer' ? trailerSpecs : truckSpecs).filter(([, v]) => v != null && String(v).trim() !== '');
  const half = Math.ceil(specs.length / 2);
  const leftSpecs = specs.slice(0, half);
  const rightSpecs = specs.slice(half);

  // Group gallery images in rows of 3
  const galleryRows = [];
  for (let i = 0; i < images.length; i += 3) {
    galleryRows.push(images.slice(i, i + 3));
  }

  return (
    <>
      <link href="https://fonts.googleapis.com/css?family=Open+Sans" rel="stylesheet" />
      <style>{`
        body {
          font-family: 'Open Sans', sans-serif !important;
          margin: 0;
        }
        * { box-sizing: border-box; }

        div[class^="col-"] {
          padding-left: 3px;
          padding-right: 3px;
        }
        .wrap {
          max-width: 900px;
          margin: 0 auto;
        }
        .row {
          display: flex;
          flex-wrap: wrap;
        }
        .row::after {
          content: '';
          display: table;
          clear: both;
        }
        .col-xs-8 { width: 66.66666667%; }
        .col-xs-4 { width: 33.33333333%; }
        .col-xs-12 { width: 100%; }
        .col-xs-3 { width: 25%; }
        .col-xs-9 { width: 75%; }

        #dealerWrap {
          padding-top: 0;
        }
        .dealerName {
          font-size: 20px;
          text-transform: uppercase;
          margin-top: 10px;
        }
        .logoWrap {
          text-align: center;
        }
        #logoImg {
          max-width: 100%;
        }
        .sep {
          border-bottom: 1px dotted #999;
          margin-bottom: 15px;
          padding-bottom: 15px;
        }
        .seprow {}
        .seprowafter {}
        .stock {
          font-weight: bold;
          text-transform: uppercase;
          font-size: 14px;
          color: #c40000;
        }
        .title {
          font-size: 18px;
          text-transform: uppercase;
          padding-bottom: 12px;
        }
        .price {
          font-size: 18px;
          text-transform: uppercase;
          padding-bottom: 12px;
          color: #c40000;
          text-align: right;
        }
        .photoWrap {
          position: relative;
          max-height: 350px;
          overflow: hidden;
          background: #f2f2f2;
          border: 1px solid #c0c0c0;
        }
        .photoWrap img {
          width: 100%;
          height: 350px;
          object-fit: contain;
          display: block;
        }
        .photoCount {
          position: absolute;
          top: 8px;
          left: 8px;
          text-align: center;
          padding: 3px 8px;
          background: #000 !important;
        }
        .photoCount a, .photoCount a:hover {
          color: #fff !important;
          text-decoration: none;
          font-size: 13px;
          font-weight: 600;
        }
        .photo {
          padding-bottom: 6px;
        }
        .photo img {
          width: 100%;
          display: block;
        }
        .gallery-img {
          width: 100%;
          height: 200px;
          object-fit: contain;
          display: block;
          background: #f2f2f2;
        }
        .noPhoto {
          width: 100%;
          height: 350px;
          background: #f2f2f2 !important;
          border: 1px solid #c0c0c0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-weight: bold;
          text-transform: uppercase;
          color: #999;
        }
        .bar {
          text-transform: uppercase;
          background: #333 !important;
          color: #fff !important;
          border: 1px solid #000;
          padding: 2px 6px;
          margin-bottom: 6px;
        }
        .desc {
          font-size: 13px;
          padding: 0 6px;
        }
        .descWrap {}

        .spec table {
          display: inline-block;
          vertical-align: top;
          width: 49.5%;
        }
        .spec td {
          padding: 1px 6px;
          font-size: 13px;
        }
        .spec td:nth-child(1) {
          background: #f2f2f2;
          border: 1px solid #d3d3d3;
          min-width: 90px;
        }
        .spec td:nth-child(2) {
          border-bottom: 1px solid #eee;
        }

        .disclaimer {
          text-align: center;
          font-style: italic;
          font-size: 11px;
          margin: 8px 0 0 0;
          color: #c40000;
        }

        div.pbreak {
          page-break-before: always;
          margin-top: 48px;
        }

        .copy {
          text-align: center;
          font-size: 11px;
          padding-top: 15px;
          font-style: italic;
        }
        .copy img {
          width: 150px;
        }

        /* CTS Footer */
        .cts-footer {
          padding: 12px 3px 0;
        }
        .cts-footer-sep {
          border-top: 1px dotted #999;
          padding-top: 12px;
          margin-top: 8px;
        }
        .cts-footer-grid {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #555;
          line-height: 1.7;
        }

        /* Print toolbar (screen only) */
        .print-bar {
          background: #f5f5f5;
          padding: 10px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #ddd;
        }
        .print-btn {
          background: #c40000;
          color: #fff;
          border: none;
          padding: 8px 20px;
          border-radius: 3px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          font-family: 'Open Sans', sans-serif;
        }
        .print-btn:hover { background: #a00; }

        @media screen and (max-width: 500px) {
          .wrap { max-width: 90% !important; }
          .col-xs-8, .col-xs-4, .col-xs-9, .col-xs-3 { width: 100%; }
          .spec table { display: block; width: 100%; }
        }

        @media print {
          .print-bar { display: none !important; }
          a[href]:after { content: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          div[class^="col-"] {
            padding-left: 15px;
            padding-right: 15px;
          }
          .spec .bar { margin-top: 10px; }
          .descWrap { padding-left: 0 !important; }
          .seprow { display: none; }
          .seprowafter { margin-top: 8px; }
          div.pbreak { margin-top: 0; }
          .wrap { max-width: 100%; }
          .cts-footer { page-break-inside: avoid; }
        }
      `}</style>

      {/* Print toolbar - hidden on print */}
      <div className="print-bar" data-testid="print-toolbar">
        <span style={{ fontSize: 13, color: '#666' }}>Spec Sheet — {title}</span>
        <button className="print-btn" onClick={() => window.print()} data-testid="print-button">
          Print Spec Sheet
        </button>
      </div>

      <div className="wrap" data-testid="spec-sheet-wrap">
        {/* Header: Dealer info + Logo */}
        <div className="row">
          <div className="col-xs-8">
            <div id="dealerWrap">
              <div className="dealerName">Canadian Truck Sales</div>
              5939 Shawson Dr Suite #100<br />
              Mississauga, ON L4W 3Y2<br />
              (416) 417-2525<br />
              <a href="https://canadiantrucksales.ca" style={{ color: '#c40000', textDecoration: 'none' }}>www.canadiantrucksales.ca</a><br />
            </div>
          </div>
          <div className="col-xs-4 logoWrap" style={{ textAlign: 'right' }}>
            <img style={{ maxHeight: 144 }} src={logoUrl} alt="Canadian Truck Sales" id="logoImg" />
          </div>
        </div>

        {/* Separator */}
        <div className="row seprow">
          <div className="col-xs-12">
            <div className="sep" />
          </div>
        </div>

        {/* Stock + Title + Price */}
        <div className="row seprowafter">
          <div className="col-xs-9 title">
            <div className="stock">Stock #{item.stock_number}</div>
            {title}
          </div>
          <div className="col-xs-3 price">
            {price}
          </div>
        </div>

        {/* Main photo + Additional Info */}
        <div className="row">
          <div className="col-xs-8 photo">
            {images.length > 0 ? (
              <div className="photoWrap">
                <img width="100%" src={resolveImageUrl(images[0])} alt={title} />
                {images.length > 1 && (
                  <div className="photoCount">
                    <a href={`/inventory/${item.id}`}>{images.length} Photos</a>
                  </div>
                )}
              </div>
            ) : (
              <div className="noPhoto">
                <img src={logoUrl} alt="CTS" style={{ width: '40%', opacity: 0.25 }} />
                <div style={{ marginTop: 12, fontSize: 16, fontWeight: 700, letterSpacing: 2, color: '#999', textTransform: 'uppercase' }}>Coming Soon</div>
              </div>
            )}
          </div>
          <div className="col-xs-4 descWrap">
            <div className="bar">Additional Information</div>
            <div className="desc">
              {item.description || 'Contact the sales team for more details about this unit.'} Call (416) 417-2525
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="row">
          <div className="col-xs-12 spec">
            <div className="bar">Specifications</div>
            <table>
              <tbody>
                {leftSpecs.map(([k, v], i) => <tr key={i}><td>{k}</td><td>{v}</td></tr>)}
              </tbody>
            </table>
            <table>
              <tbody>
                {rightSpecs.map(([k, v], i) => <tr key={i}><td>{k}</td><td>{v}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTS Footer with contact + hours */}
        <div className="cts-footer">
          <div className="cts-footer-sep" />
          <div className="cts-footer-grid">
            <div>
              <strong style={{ color: '#222' }}>Canadian Truck Sales</strong><br />
              5939 Shawson Dr Suite #100<br />
              Mississauga, ON L4W 3Y2<br />
              (416) 417-2525 | Toll-free: (844) 417-2525<br />
              info@canadiantrucksales.ca
            </div>
            <div style={{ textAlign: 'right' }}>
              <strong style={{ color: '#222' }}>Business Hours</strong><br />
              Monday - Friday: 09:00 AM – 06:00 PM<br />
              Saturday: 10:00 AM – 05:00 PM<br />
              Sunday: Closed
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
